/* Copyright (c) 2020-present CloudIO Inc */

import { Page } from '../../registry/ComponentRegistry';
import { pageAtom } from 'designer/atoms';
import {
  AnalyzeContext,
  AnalyzeIssue,
  AnalyzeResult,
} from 'layout/Sidebar/panels/TreeView/AnalyzeUtils';
import { Item } from '../../registry/Component.type';

function getRootTransaction(
  page: Page,
  itm: Item<'Transaction'>,
): Item<'Transaction'> | null {
  const parent = page[itm.parent];
  if (!parent) return null;
  if (parent.type !== 'Transaction') {
    return itm;
  }
  return getRootTransaction(page, parent as Item<'Transaction'>);
}

export async function analyzeDataStore({
  platform,
  appUid,
  pageId,
  itemId,
  mgr,
}: AnalyzeContext): Promise<AnalyzeResult> {
  const alerts: AnalyzeIssue[] = [];
  const autoFixes: (() => void)[] = [];
  const page = platform.getPage(appUid, pageId);
  if (!page) {
    return null;
  }
  const item = page[itemId] as Item<'DataStore'>;
  const itemIds = Object.keys(page);
  let hasSaveAction =
    itemIds
      .filter((id) => page[id]?.type === 'ActionSave')
      .map((id) => page[id] as Item<'ActionSave'>)
      .filter((itm) => itm.props.alias === item.props.alias).length > 0;

  if (!hasSaveAction) {
    hasSaveAction =
      itemIds
        .filter((id) => page[id]?.type === 'ActionButton')
        .map((id) => page[id] as Item<'ActionButton'>)
        .filter((itm) => itm.props.alias === item.props.alias).length > 0;
  }

  if (!hasSaveAction) {
    const txnCodes = itemIds
      .filter((id) => page[id]?.type === 'Transaction')
      .map((id) => page[id] as Item<'Transaction'>)
      .filter((itm) => itm.props.alias === item.props.alias)
      .map((txn) => getRootTransaction(page, txn))
      .filter((txn) => !!txn)
      .map((txn) => txn?.props.transactionCode);

    hasSaveAction =
      itemIds
        .filter((id) => page[id]?.type === 'ActionSaveTransaction')
        .map((id) => page[id] as Item<'ActionSaveTransaction'>)
        .filter((itm) => txnCodes.includes(itm.props.transactionCode)).length >
      0;
  }

  if (!hasSaveAction) {
    const fix = () => {
      const latestPage = { ...platform.getPage(appUid, pageId) };
      if (!latestPage) {
        return;
      }
      const ds = latestPage[itemId] as Item<'DataStore'>;
      const { insertable, updatable, deletable, queryable, ...others } =
        ds.props;
      const rods = {
        ...latestPage[itemId],
        props: others,
        type: 'ReadOnlyDataStore',
      } as Item<'ReadOnlyDataStore'>;
      latestPage[itemId] = rods;
      platform.setter(pageAtom, latestPage, {
        params: { appUid, pageId },
      });
    };
    autoFixes.push(fix);
    alerts.push({
      key: `ro-${itemId}`,
      fix,
      alert: `No save action exists for Data Store ${item.props.alias}.`,
      itemId,
      severity: 'info',
      showItemId: item.itemId,
      tip: 'Click Fix It to convert this store into a Readonly Data Store.',
    });
  }

  return [alerts, autoFixes];
}
